// Globals
import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import invariant from 'tiny-invariant';
import FocusRing from '@atlaskit/focus-ring';
import { type Instruction, type ItemMode } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';

// Components
import {
  StyledChevronWrapper,
  StyledTitle,
  StyledTreeItemWrapper,
  StyledButton,
  StyledChildrenWrapper,
  StyledPreviewWrapper,
  StyledTreeItemTitleWrapper,
} from '../../components/CategoryItem/CategoryItemStyles';
import { ChevronIcon } from '@/icons';

// Helpers
import { type TreeItem as TreeItemType } from './data';
import { indentPerLevel } from './constants';
import { DependencyContext, TreeContext } from './treeContext';

function delay({ waitMs: timeMs, fn }: { waitMs: number; fn: () => void }): () => void {
  let timeoutId: number | null = window.setTimeout(() => {
    timeoutId = null;
    fn();
  }, timeMs);
  return function cancel() {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
}

const TreeItem = memo(function TreeItem({
  item,
  mode,
  level,
  index,
}: {
  item: TreeItemType;
  mode: ItemMode;
  level: number;
  index: number;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [state, setState] = useState<'idle' | 'dragging' | 'preview' | 'parent-of-instruction'>('idle');
  const [instruction, setInstruction] = useState<Instruction | null>(null);
  const cancelExpandRef = useRef<(() => void) | null>(null);

  const { dispatch, uniqueContextId, getPathToItem, registerTreeItem } = useContext(TreeContext);
  const { DropIndicator, attachInstruction, extractInstruction } = useContext(DependencyContext);
  const toggleOpen = useCallback(() => {
    dispatch({ type: 'toggle', itemId: item.id });
  }, [dispatch, item]);

  useEffect(() => {
    invariant(buttonRef.current);
    return registerTreeItem({
      itemId: item.id,
      element: buttonRef.current,
    });
  }, [item.id, registerTreeItem]);

  const cancelExpand = useCallback(() => {
    cancelExpandRef.current?.();
    cancelExpandRef.current = null;
  }, []);

  const clearParentOfInstructionState = useCallback(() => {
    setState((current) => (current === 'parent-of-instruction' ? 'idle' : current));
  }, []);

  useEffect(() => {
    invariant(buttonRef.current);

    return combine(
      draggable({
        element: buttonRef.current,
        getInitialData: () => ({
          id: item.id,
          type: 'tree-item',
          isOpenOnDragStart: item.isOpen,
          uniqueContextId,
        }),
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
            render: ({ container }) => {
              const root = createRoot(container);
              root.render(<StyledPreviewWrapper>{item.name}</StyledPreviewWrapper>);

              return () => root.unmount();
            },
            nativeSetDragImage,
          });
        },
        onDragStart: ({ source }) => {
          setState('dragging');
          // collapse open items during a drag
          if (source.data.isOpenOnDragStart) {
            dispatch({ type: 'collapse', itemId: item.id });
          }
        },
        onDrop: ({ source }) => {
          setState('idle');
          if (source.data.isOpenOnDragStart) {
            dispatch({ type: 'expand', itemId: item.id });
          }
        },
      }),
      dropTargetForElements({
        element: buttonRef.current,
        getData: ({ input, element }) => {
          const data = { id: item.id };

          return attachInstruction(data, {
            input,
            element,
            indentPerLevel,
            currentLevel: level,
            mode,
            block: ['make-child'],
          });
        },
        canDrop: (args) => {
          const { source } = args;

          // Ensure the source and target are from the same context
          if (source.data.type !== 'tree-item' || source.data.uniqueContextId !== uniqueContextId) {
            return false;
          }

          if (typeof source.data.id !== 'string') {
            return false;
          }

          // Get the source item's parent ID
          const sourcePath = getPathToItem(source.data.id);
          const sourceParentId = sourcePath[sourcePath.length - 1] ?? ''; // Root level if no parent

          // Get the target item's parent ID
          const targetPath = getPathToItem(item.id);
          const targetParentId = targetPath[targetPath.length - 1] ?? ''; // Root level if no parent

          // Fix for a bug when dragging an item to an open sibling is not possible
          if (item.isOpen && sourceParentId === targetParentId) {
            dispatch({ type: 'toggle', itemId: item.id });
          }

          // Allow drop only if the source and target share the same parent
          return sourceParentId === targetParentId;
        },
        getIsSticky: () => true,
        onDrag: ({ self, source }) => {
          const instructionExtracted = extractInstruction(self.data);

          if (source.data.id !== item.id) {
            // expand after 500ms if still merging
            if (
              instructionExtracted?.type === 'make-child' &&
              item.children.length &&
              !item.isOpen &&
              !cancelExpandRef.current
            ) {
              cancelExpandRef.current = delay({
                waitMs: 500,
                fn: () => dispatch({ type: 'expand', itemId: item.id }),
              });
            }
            if (instructionExtracted?.type !== 'make-child' && cancelExpandRef.current) {
              cancelExpand();
            }

            setInstruction(instructionExtracted);
            return;
          }
          if (instructionExtracted?.type === 'reparent') {
            setInstruction(instructionExtracted);
            return;
          }
          setInstruction(null);
        },
        onDragLeave: () => {
          cancelExpand();
          setInstruction(null);
        },
        onDrop: () => {
          cancelExpand();
          setInstruction(null);
        },
      }),
      monitorForElements({
        canMonitor: ({ source }) => source.data.uniqueContextId === uniqueContextId,
        onDrop() {
          clearParentOfInstructionState();
        },
      }),
    );
  }, [
    dispatch,
    item,
    mode,
    level,
    cancelExpand,
    uniqueContextId,
    extractInstruction,
    attachInstruction,
    getPathToItem,
    clearParentOfInstructionState,
  ]);

  useEffect(
    function mount() {
      return function unmount() {
        cancelExpand();
      };
    },
    [cancelExpand],
  );

  const canBeOpen = item.children.length > 0;
  const isFirstLevel = level === 0;

  const aria = (() => {
    if (!canBeOpen) {
      return undefined;
    }
    return {
      'aria-expanded': item.isOpen,
      'aria-controls': `tree-item-${item.id}--subtree`,
    };
  })();

  return (
    <>
      <StyledTreeItemWrapper>
        <FocusRing isInset>
          <StyledButton
            {...aria}
            id={`tree-item-${item.id}`}
            onClick={toggleOpen}
            ref={buttonRef}
            type="button"
            data-index={index}
            data-level={level}
            data-testid={`tree-item-${item.id}`}
            $level={level}
          >
            <StyledTreeItemTitleWrapper
              $isDragging={state === 'dragging'}
              $isParentOfInstruction={state === 'parent-of-instruction'}
            >
              <StyledChevronWrapper>
                {canBeOpen && (
                  <ChevronIcon
                    style={{
                      width: '1.25rem',
                      fill: '#000',
                      ...(item.isOpen && { transform: `rotate(180deg)` }),
                    }}
                  />
                )}
              </StyledChevronWrapper>
              <StyledTitle $isFirstLevel={isFirstLevel}>{item.name}</StyledTitle>
            </StyledTreeItemTitleWrapper>

            {instruction ? <DropIndicator instruction={instruction} /> : null}
          </StyledButton>
        </FocusRing>
      </StyledTreeItemWrapper>

      {canBeOpen && item.isOpen ? (
        <StyledChildrenWrapper id={aria?.['aria-controls']}>
          {item.children.map((child, i, array) => {
            const childType: ItemMode = (() => {
              if (child.children.length && child.isOpen) {
                return 'expanded';
              }

              if (i === array.length - 1) {
                return 'last-in-group';
              }

              return 'standard';
            })();
            return <TreeItem item={child} key={child.id} level={level + 1} mode={childType} index={index} />;
          })}
        </StyledChildrenWrapper>
      ) : null}
    </>
  );
});

export default TreeItem;
